import axios from "axios";

declare global {
    var appConfig: any;
}

export interface BrowserJsData {
    language: string;
    colorDepth: number;
    screenHeight: number;
    screenWidth: number;
    timezone: number;
    javaEnabled: boolean;
}

export interface TipProcessRequest {
    trackingTipId: string;
    browserJsData: BrowserJsData;
}

export interface ActionsEndpointDefinition {
    url: string;
    method?: string;
    vars?: any;
}

export interface TipProcessResponse {
    outcome: "SUCCESS" | "FAILURE" | "ACTION_REQUIRED";
    actionsEndpoint?: ActionsEndpointDefinition;
}

export interface GooglePayTipInitRequest {
    shareKey: string;
    shareKeyType: ShareKeyType;
    price: TipPrice;
    redirectUri: string;
    paymentToken: string;
}

export interface TipPrice {
    amount: number;
    ccy: string;
}

export interface TipPriceString {
    amount: string;
    ccy: string;
}

export type ShareKeyType = "DELIVERY_STOP" | "DELIVERY_ORDERER";

export interface CardTipInitRequest {
    language: string;
    price: TipPrice;
    redirectUri: string;
    shareKey: string;
    shareKeyType: ShareKeyType;
}

export interface CardTipInitResponse {
    processUri: string;
}

export interface ApplePayValidateSessionRequest {
    validationUri: string;
    language: string;
    tenant: string;
}

export interface ApplePayValidateSessionResponse {
    sessionData: string;
}

export interface ApplePayInitTipPaymentRequest {
    shareKey: string;
    shareKeyType: ShareKeyType;
    price: TipPrice;
    redirectUri: string;
    paymentDataBase64: string;
}

export interface InitTipPaymentResponse {
    trackingTipId: string;
    fingerPrintUri?: string;
}

export interface CsobGooglePayInitParams {
    apiVersion: number;
    apiVersionMinor: number;
    paymentMethodType: google.payments.api.PaymentMethodType;
    allowedCardNetworks: google.payments.api.CardNetwork[];
    allowedCardAuthMethods: google.payments.api.CardAuthMethod[];
    assuranceDetailsRequired: boolean;
    billingAddressRequired: boolean;
    billingAddressParametersFormat: string;
    tokenizationSpecificationType: google.payments.api.PaymentMethodTokenizationType;
    gateway: string;
    gatewayMerchantId: string;
    googlepayMerchantId: string;
    merchantName: string;
    environment: "TEST" | "PRODUCTION";
    totalPriceStatus: string;
    countryCode: string;
}

export interface CsobGooglePayInitParamsResponse {
    initParams: CsobGooglePayInitParams;
}

export interface ApplePayInitParams {
    countryCode: string;
    supportedNetworks: string[];
    merchantCapabilities: ApplePayJS.ApplePayMerchantCapability[];
}

export interface ApplePayInitParamsResponse {
    displayName: string;
    initParams: ApplePayInitParams;
}

export interface TrackingTip {
    id?: string;
    stopTrackingKey: string;
    paymentId: number;
    rideId: string;
    deliveryStopId: string;
    price: TipPriceString;
    receiptPdfId?: string;
    redirectUri: string;
    created: string;
}

export interface CsobCallbackData {
    payId: string;
    dttm: string;
    resultCode: number;
    resultMessage: string;
    paymentStatus: number;
    authCode?: string;
    merchantData: string;
    statusDetail?: string;
    signature: string;
}

export async function loadGooglePayInitParams(): Promise<CsobGooglePayInitParamsResponse> {
    const response = await axios.get<CsobGooglePayInitParamsResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/googlepay/initParams`
    );

    return response.data;
}

export async function googlePayProcess(body: TipProcessRequest): Promise<TipProcessResponse> {
    const response = await axios.post<TipProcessResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/googlepay/process`,
        body
    );

    return response.data;
}

export async function googlePayInit(
    body: GooglePayTipInitRequest
): Promise<InitTipPaymentResponse> {
    const response = await axios.post<InitTipPaymentResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/googlepay/init`,
        body
    );

    return response.data;
}

export async function payByCard(body: CardTipInitRequest): Promise<CardTipInitResponse> {
    const response = await axios.post<CardTipInitResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/card`,
        body
    );

    return response.data;
}

export async function applePayInitParams(language: string): Promise<ApplePayInitParamsResponse> {
    const response = await axios.get<ApplePayInitParamsResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/applepay/initParams`,
        {params: {language}}
    );

    return response.data;
}

export async function validateSession(
    body: ApplePayValidateSessionRequest
): Promise<ApplePayValidateSessionResponse> {
    const response = await axios.post<ApplePayValidateSessionResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/applepay/validateSession`,
        body
    );

    return response.data;
}

export async function processPayment(body: TipProcessRequest): Promise<TipProcessResponse> {
    const response = await axios.post<TipProcessResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/applepay/process`,
        body
    );

    return response.data;
}

export async function initializePayment(
    body: ApplePayInitTipPaymentRequest
): Promise<InitTipPaymentResponse> {
    const response = await axios.post<InitTipPaymentResponse>(
        `${appConfig.baseApiUrl}/tracking/tips/applepay/init`,
        body
    );

    return response.data;
}

export async function csobCallback(callbackData: CsobCallbackData) {
    const response = await axios.get(`${appConfig.baseApiUrl}/tracking/tips/callback`, {
        params: {callbackData},
    });

    return response.data;
}

export async function getTrackingTip(
    shareKeyType: ShareKeyType,
    shareKey: string
): Promise<TrackingTip[]> {
    const response = await axios.get<TrackingTip[]>(`${appConfig.baseApiUrl}/tracking/tips/paid`, {
        params: {shareKeyType, shareKey},
    });

    return response.data;
}

export async function getRidePaymentReceiptUrl(
    shareKeyType: ShareKeyType,
    shareKey: string
): Promise<string> {
    const response = await axios.get(`${appConfig.baseApiUrl}/tracking/tips/receipt/link`, {
        params: {shareKeyType, shareKey},
    });

    return response.data;
}
